import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { socialLinks } from '../data/mock';
import { smoothScrollToElement } from '../lib/smoothScroll';

const LOGO_URL = "/media/logo.png";

const XLogo = ({ size = 22 }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.847h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.153h7.594l5.243 6.932 6.064-6.932Zm-1.292 19.493h2.039L6.486 3.24H4.298l13.311 17.406Z" />
  </svg>
);

const TikTokLogo = ({ size = 22 }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07Z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'About', href: '#about' },
    { name: 'Team', href: '#team' },
    { name: 'Events', href: '#events' },
    { name: 'Connect', href: '#connect' }
  ];

  const socialItems = [
    {
      name: 'Facebook',
      href: socialLinks.facebook,
      icon: Facebook,
      className: 'hover:bg-blue-600'
    },
    {
      name: 'Instagram',
      href: socialLinks.instagram,
      icon: Instagram,
      className: 'hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500'
    },
    {
      name: 'X',
      href: socialLinks.twitter,
      icon: XLogo,
      className: 'hover:bg-white hover:text-black'
    },
    {
      name: 'TikTok',
      href: socialLinks.tiktok,
      icon: TikTokLogo,
      className: 'hover:bg-pink-600'
    },
    {
      name: 'YouTube',
      href: socialLinks.youtube,
      icon: Youtube,
      className: 'hover:bg-red-600'
    }
  ];

  const scrollToSection = (href) => {
    smoothScrollToElement(href);
  };

  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
            >
              <img
                src={LOGO_URL}
                alt="Heartland Plains Chasing"
                className="h-24 w-auto mb-6"
              />
              <p className="text-gray-400 max-w-md mb-6 leading-relaxed">
                Documenting some of nature's most powerful moments since 2026. Join us as we chase storms across the Plains to capture severe weather responsibly.
              </p>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-white font-bold text-lg mb-6">Quick Links</h3>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
                {footerLinks.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-left text-gray-400 hover:text-red-400 transition-colors duration-200"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Social */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ amount: 0.2 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-white font-bold text-lg mb-6">Follow Us</h3>
              <div className="flex flex-wrap gap-4 mb-8">
                {socialItems.map(({ name, href, icon: Icon, className }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow Heartland Plains Chasing on ${name}`}
                    title={name}
                    className={`w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200 ${className}`}
                  >
                    <Icon size={22} />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {currentYear} Heartland Plains Chasing. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
